<script setup>
const client = useSupabaseClient();
const user = useState('user');

const { transitionColour } = useTailwindConfig();

const isFormSubmitted = ref(false);
const selectedFiles = ref([]);

const formFieldsState = useState('formFields');
if (!formFieldsState.value) {
  formFieldsState.value = {
    name: user.value.first_name + ' ' + user.value.last_name,
    problem: "",
    email: user.value.email,
    address: "",
    formSuccess: false,
    submitLoading: false,
    uploadedFileUrls: [],
    problems: [
      'Technical/UI Issue',
      'Zoning Bylaw Information Error',
      'Feature Request or Suggestion',
      'Other'
    ],
    technicalLocations: [
      'Planner Dashboard',
      'ADU Module',
      'Shed Module',
      'Deck Module',
      'Login',
      'Permitted Uses',
      'Property Summary Table',
      'Search Page',
      'Other'
    ],
    technicalIssueLocation: "",
    technicalIssueDescription: "",
    zoningBylawErrors: "",
    zoningBylawSuggestions: "",
    featureSuggestions: "",
    otherSuggestions: ""
  };
}

// Use the route to get the address from the URL path
const route = useRoute();
const pathSegments = route.path.split('/');
const potentialAddress = pathSegments[pathSegments.length - 1];

// Check if the last segment of the path is an address and set it
if (potentialAddress && /^\d/.test(potentialAddress)) {
  formFieldsState.value.address = decodeURIComponent(potentialAddress);

  // Replace dashes with spaces
  formFieldsState.value.address = formFieldsState.value.address.replace(/-/g, ' ');
}

async function submitForm() {
  formFieldsState.value.submitLoading = true;

  try {
    const problemsData = {
      problem: formFieldsState.value.problem,
      technicalIssueLocation: formFieldsState.value.technicalIssueLocation,
      technicalIssueDescription: formFieldsState.value.technicalIssueDescription,
      zoningBylawErrors: formFieldsState.value.zoningBylawErrors,
      zoningBylawSuggestions: formFieldsState.value.zoningBylawSuggestions,
      featureSuggestions: formFieldsState.value.featureSuggestions,
      otherSuggestions: formFieldsState.value.otherSuggestions
    };

    formFieldsState.value.uploadedFileUrls = await uploadFiles();

    // Add the uploaded file URLs to the problem data
    if (formFieldsState.value.uploadedFileUrls.length > 0) {
      problemsData.uploadedFileUrls = formFieldsState.value.uploadedFileUrls;
    }

    await client
      .from("feedback_form")
      .insert({
        name: formFieldsState.value.name,
        email: formFieldsState.value.email,
        address: formFieldsState.value.address,
        problem: JSON.stringify(problemsData),
      });

    await emailFounders();
    formFieldsState.value.formSuccess = true;
  } catch (error) {
    console.error("Error submitting form:", error);
    formFieldsState.value.formSuccess = false;
  } finally {
    formFieldsState.value.submitLoading = false;
    isFormSubmitted.value = true;
  }
}

async function uploadFiles() {
  if (!selectedFiles.value?.length > 0) {
    return [];
  }

  const urls = [];
  for (const file of selectedFiles.value) {
    const uniqueFileName = `${user.value.id}/${file.name}`;
    const { data, error } = await client.storage
      .from('feedback-form-files')
      .upload(uniqueFileName, file);

    if (error) {
      // If status code is 409, it means this file already exists. Use the existing file and don't throw error
      if (error.statusCode == '409') {
        const file = await client.storage
          .from('feedback-form-files')
          .getPublicUrl(uniqueFileName);
        urls.push(file.data.publicUrl);
        console.log('File already exists, using existing file');
        continue;
      }
      else {
        console.error('Error uploading file:', error);
        throw error;
      }
    }

    const publicUrl = client.storage
      .from('feedback-form-files')
      .getPublicUrl(data.path).data.publicUrl;

    urls.push(publicUrl);
  }
  return urls;
}

async function emailFounders() {
  const emailTo = 'founders@siteability.ca';
  const subject = "New 'Problem' Submission";

  let messageHtml = "";

  if (formFieldsState.value.name) {
    messageHtml += `<p>Name: ${formFieldsState.value.name}</p>`;
  }
  if (formFieldsState.value.email) {
    messageHtml += `<p>Email: ${formFieldsState.value.email}</p>`;
  }
  if (formFieldsState.value.problem) {
    messageHtml += `<p>Problem: ${formFieldsState.value.problem}</p>`;
  }
  if (formFieldsState.value.address) {
    messageHtml += `<p>Address: ${formFieldsState.value.address}</p>`;
  }
  if (formFieldsState.value.technicalIssueLocation) {
    messageHtml += `<p>Technical Issue Location: ${formFieldsState.value.technicalIssueLocation}</p>`;
  }
  if (formFieldsState.value.technicalIssueDescription) {
    messageHtml += `<p>Technical Issue Description: ${formFieldsState.value.technicalIssueDescription}</p>`;
  }
  if (formFieldsState.value.zoningBylawErrors) {
    messageHtml += `<p>Zoning Bylaw Errors: ${formFieldsState.value.zoningBylawErrors}</p>`;
  }
  if (formFieldsState.value.zoningBylawSuggestions) {
    messageHtml += `<p>Zoning Bylaw Suggestions: ${formFieldsState.value.zoningBylawSuggestions}</p>`;
  }
  if (formFieldsState.value.featureSuggestions) {
    messageHtml += `<p>Feature Suggestions: ${formFieldsState.value.featureSuggestions}</p>`;
  }
  if (formFieldsState.value.otherSuggestions) {
    messageHtml += `<p>Other Suggestions: ${formFieldsState.value.otherSuggestions}</p>`;
  }
  if (formFieldsState.value.uploadedFileUrls.length > 0) {
    const fileLinks = formFieldsState.value.uploadedFileUrls.map(url => `<a href="${url}" target="_blank">${url}</a>`).join('<br>');
    messageHtml += `<p>Uploaded Files:<br>${fileLinks}</p>`;
  }

  try {
    const emailResult = await useFetch("/api/email", {
      params: {
        email: emailTo,
        subject,
        messageHtml
      }
    });

    if (emailResult.data.error) {
      throw new Error(emailResult.data.error);
    }
  } catch (error) {
    console.error("Error sending email:", error);
    formFieldsState.value.formSuccess = false;
  }
}
</script>

<template>
  <div class="max-h-[75vh] overflow-y-auto overflow-x-hidden flex flex-col space-y-4 px-1">
    <div v-if="isFormSubmitted" class="flex justify-center items-center text-lg p-4 rounded-lg bg-gray-100">
      <p v-if="formFieldsState.formSuccess">Thanks for the feedback. We'll get to it as soon as we can.</p>
      <p class="text-red-700" v-else>There was an error sending your suggestion. Please try again later.</p>
    </div>

    <template v-else>
      <p class="text-sm">
        If you run into any issues — whether it's a technical bug, a UI issue, incorrect bylaw information, or if you
        have suggestions for new features - let us know so we can keep improving the tool!
      </p>

      <UFormField label="Name">
        <UInput v-model="formFieldsState.name" disabled class="opacity-75 w-full" />
      </UFormField>

      <UFormField label="Address">
        <UInput v-model="formFieldsState.address"
          :placeholder="formFieldsState.address ? formFieldsState.address : 'Address'" class="w-full" />
      </UFormField>

      <!-- Step 1 -->
      <UFormField label="What type of feedback are you submitting?" required>
        <USelectMenu v-model="formFieldsState.problem" placeholder="Select A Category" :items="formFieldsState.problems"
          class="w-full" />
      </UFormField>

      <!-- Step 2 -->
      <UFormField v-if="formFieldsState.problem == 'Technical/UI Issue'" label="Where did you experience this issue?"
        required>
        <USelectMenu v-model="formFieldsState.technicalIssueLocation" :items="formFieldsState.technicalLocations"
          placeholder="Select A Category" class="w-full" />
      </UFormField>

      <!-- Step 3 -->
      <template
        v-if="(formFieldsState.problem == 'Technical/UI Issue' && formFieldsState.technicalIssueLocation) || (formFieldsState.problem != 'Technical/UI Issue' && formFieldsState.problem)">
        <UFormField v-if="formFieldsState.problem === 'Technical/UI Issue'" label="Explain the issue" required>
          <TiptapEditor v-model="formFieldsState.technicalIssueDescription" placeholder="Explain the issue..." />
        </UFormField>
        <template v-else-if="formFieldsState.problem === 'Zoning Bylaw Information Error'">
          <UFormField label="Explain what seems incorrect or needs updating. (include any applicable clause numbers)"
            required>
            <UTextarea v-model="formFieldsState.zoningBylawErrors" autoresize class="w-full" />
          </UFormField>
          <UFormField label="How should this be corrected? (include any applicable clause numbers)">
            <UTextarea v-model="formFieldsState.zoningBylawSuggestions" autoresize class="w-full" />
          </UFormField>
        </template>
        <UFormField v-else-if="formFieldsState.problem === 'Feature Request or Suggestion'"
          label="Describe the feature or suggestion" required>
          <UTextarea v-model="formFieldsState.featureSuggestions" autoresize class="w-full" />
        </UFormField>
        <UFormField v-else-if="formFieldsState.problem === 'Other'" label="Describe the issue or suggestion" required>
          <UTextarea v-model="formFieldsState.otherSuggestions" autoresize class="w-full" />
        </UFormField>

        <UFormField
          v-if="(formFieldsState.problem === 'Technical/UI Issue' || formFieldsState.problem === 'Zoning Bylaw Information Error' || formFieldsState.problem === 'Other')"
          label="Upload Screenshots">
          <ImageUploader v-model="selectedFiles" @error="formFieldsState.formSuccess = false" />
        </UFormField>

        <!-- Submit -->
        <UButton @click="submitForm()" :loading="formFieldsState.submitLoading" :class="transitionColour"
          :disabled="!(formFieldsState.technicalIssueDescription || formFieldsState.featureSuggestions || formFieldsState.zoningBylawErrors || formFieldsState.otherSuggestions)"
          size="lg" block>
          Submit
        </UButton>
      </template>
    </template>
  </div>
</template>